import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface UiState {
  isLoading: boolean;
  isLoadingSmall: boolean;
  loadingText?: string;
  activeRequests: number;
  theme: 'dark' | 'light';
}

const initialState: UiState = {
  isLoading: false,
  isLoadingSmall: false,
  loadingText: 'Loading...',
  activeRequests: 0,
  theme: (localStorage.getItem('theme') as 'dark' | 'light') || 'dark',
};

const excludedActions = [
  'referral',
  'onlineUsers',
  'sites/fetchSites',
  'users/fetchUsers',
  'users/fetchUserById',
  'promoCodes/fetchPromoCodes',
  'auditLogs/fetchAuditLogs',
  'transactions/fetchDepositTransactions',
  'transactions/fetchPendingWithdrawals',
  'transactions/fetchWithdrawTransactions',
  'paymentProviders/fetchAll',
  'paymentSettings/fetchAll',
  'bonuses/fetchBonuses',
  'bonuses/updateBonus',
  'bonuses/updateBonusImage',
  'bonuses/toggleBonusStatus',
  'siteSocials/fetchSiteSocials',
];

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    showLoader: (
      state,
      action: PayloadAction<{ text?: string; isLoaderSmall?: boolean } | undefined>,
    ) => {
      if (action.payload?.isLoaderSmall) {
        state.isLoadingSmall = true;
      } else {
        state.isLoading = true;
      }
      state.loadingText = action.payload?.text || 'Loading...';
      state.activeRequests++;
    },
    hideLoader: (state) => {
      state.activeRequests--;
      if (state.activeRequests <= 0) {
        state.activeRequests = 0;
        state.isLoading = false;
        state.isLoadingSmall = false;
        state.loadingText = 'Loading...';
      }
    },
    showLoaderSmall: (state) => {
      state.isLoadingSmall = true;
      state.activeRequests++;
    },
    hideLoaderSmall: (state) => {
      state.activeRequests--;
      if (state.activeRequests <= 0) {
        state.activeRequests = 0;
        state.isLoadingSmall = false;
      }
    },
    toggleTheme: (state) => {
      state.theme = state.theme === 'dark' ? 'light' : 'dark';
      localStorage.setItem('theme', state.theme);
    },
    setTheme: (state, action: PayloadAction<'dark' | 'light'>) => {
      state.theme = action.payload;
      localStorage.setItem('theme', state.theme);
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        (action) => action.type.endsWith('/pending'),
        (state, action) => {
          state.activeRequests++;
          const isExcluded = excludedActions.some((excluded) => action.type.includes(excluded));
          if (!isExcluded) {
            state.isLoading = true;
          }

          if (action.type.includes('login')) {
            state.loadingText = 'Logging in...';
          } else if (action.type.includes('register')) {
            state.loadingText = 'Registering...';
          } else {
            state.loadingText = 'Loading...';
          }
        },
      )
      .addMatcher(
        (action) => action.type.endsWith('/fulfilled') || action.type.endsWith('/rejected'),
        (state, action) => {
          state.activeRequests--;
          if (state.activeRequests <= 0) {
            state.activeRequests = 0;
            state.isLoading = false;
            state.loadingText = 'Loading...';
          }
        },
      );
  },
});

export const { showLoader, hideLoader, showLoaderSmall, hideLoaderSmall, toggleTheme, setTheme } =
  uiSlice.actions;

export default uiSlice.reducer;
